// Cogo KG browser core loader.
//
// Adapts the cogo-kg-worker init pattern for browser runtime. Key differences
// from the Cloudflare Workers variant (see WASM doc §9):
//   1. SAB detection: CF Workers have SharedArrayBuffer by default; browsers
//      require cross-origin isolation (COOP/COEP) — we detect at runtime and
//      log which path Oxigraph picks (observational for sub-step 6; Oxigraph
//      itself chooses internally based on availability).
//   2. SIMD detection: Oxigraph 0.5.x ships a single WASM binary that uses
//      SIMD opcodes. Browsers without SIMD support (old Safari) will fail
//      to instantiate — we detect pre-init and surface a clear error instead
//      of a cryptic WebAssembly.CompileError.
//   3. WASM fetch: the browser loader fetches the binary from a configurable
//      URL (defaults to a bundled-asset URL or a caller-provided absolute
//      URL for CDN delivery). Worker uses wrangler's CompiledWasm import.
//
// Public API: initKgCore(opts) + getStore() + isReady().

// Oxigraph browser ESM exports `initSync` (accepts a Module), the default
// async `__wbg_init` (accepts URL/Response/BufferSource), `Store`, `parse`.
// We use the async default for browser cold-start (no prebuilt Module).
// Pin the `web.js` browser entry explicitly — the package.json main points
// to node.js by default; `browser` field resolves web.js at bundle time.
//
// IMPORTANT: types-only static import + lazy dynamic load. tsx (the loader
// behind this package's pretest hooks and various scripts) mishandles
// static named imports from oxigraph 0.5.6's `web.js` when the surrounding
// graph also imports any `node:` builtin — instantiation fails with
// "does not provide an export named 'Store'" even though plain `node` and
// vitest both resolve the same module correctly. Deferring the binding
// to runtime via dynamic import sidesteps that codepath without changing
// browser behaviour (Vite bundles dynamic imports as separate chunks
// the same way it would handle a static import here).
import type { Store as OxigraphStore } from "oxigraph/web.js"

import { setShapeSha, KG_BROWSER_CORE_VERSION, OXIGRAPH_VERSION } from "./version"

type OxiWebModule = typeof import("oxigraph/web.js")
let oxiWebMod: OxiWebModule | null = null

/**
 * Return the loaded `oxigraph/web.js` module. Throws if `initKgCore` has
 * not yet resolved — graph-scope helpers and any sync caller of oxigraph
 * primitives must run after init.
 */
export function getOxiWeb(): OxiWebModule {
  if (!oxiWebMod) {
    throw new Error(
      "[cogo-kg-browser] oxigraph/web.js accessed before initKgCore — call initKgCore first.",
    )
  }
  return oxiWebMod
}

export interface InitKgCoreOpts {
  /**
   * URL to the Oxigraph WASM binary. Should be served as
   * `application/wasm` with `Cache-Control: immutable` from a content-hashed
   * path (CDN). If omitted, the loader assumes `oxigraph/web_bg.wasm` is
   * resolvable by the bundler (dev/node path).
   */
  wasmUrl?: string | URL

  /**
   * Pre-fetched WASM bytes (BufferSource) or compiled Module. Use this in
   * tests or when the caller already has the WASM available (e.g. service
   * worker cache hit).
   */
  wasmSource?: BufferSource | WebAssembly.Module

  /**
   * Raw TTL strings to hydrate into the core store on init. Order-independent
   * (oxigraph parse+add is commutative); worker convention is
   * capabilities → events → actions for dependency clarity.
   */
  ttlSources?: Array<{ name: string; ttl: string }>

  /** Optional SHACL shapes TTL string. Stored for later shacl-runner use. */
  shapesTtl?: string

  /** If true, detection diagnostics are logged via console.debug. */
  verbose?: boolean
}

export interface CoreCapabilities {
  /** Cross-origin isolated (COOP/COEP headers set by host page). */
  crossOriginIsolated: boolean
  /** SharedArrayBuffer available in this context. */
  sharedArrayBuffer: boolean
  /** WASM SIMD supported by this runtime. */
  wasmSimd: boolean
  /** WASM bulk-memory supported (needed by Oxigraph). */
  wasmBulkMemory: boolean
  /** Reference path picked: "fast" (SAB) / "standard" (no SAB, SIMD) / "fallback" (no SIMD) / "unknown". */
  chosenPath: "fast" | "standard" | "fallback" | "unknown"
}

export interface InitResult {
  capabilities: CoreCapabilities
  loadedTriples: number
  shapeSha: string
  /** Total init wall time (ms), including WASM compile + hydrate. */
  initMs: number
  /** WASM compile time alone (ms). */
  wasmMs: number
  /** TTL hydrate time alone (ms). */
  hydrateMs: number
}

// ---------------------------------------------------------------------------
// Feature detection (WASM doc §9.1 / §9.2)
// ---------------------------------------------------------------------------

/**
 * Detect SharedArrayBuffer. Availability requires cross-origin isolation
 * (COOP: same-origin + COEP: require-corp) on the host document. We check
 * both the symbol presence and `crossOriginIsolated`.
 */
export function detectSAB(): { available: boolean; crossOriginIsolated: boolean } {
  const hasSAB =
    typeof SharedArrayBuffer !== "undefined" &&
    typeof (globalThis as any).SharedArrayBuffer === "function"
  // `crossOriginIsolated` is a Window-only global. In Node/Vitest it's undefined.
  const coi = typeof (globalThis as any).crossOriginIsolated === "boolean"
    ? (globalThis as any).crossOriginIsolated
    : false
  return { available: hasSAB && coi, crossOriginIsolated: coi }
}

/**
 * Detect WASM SIMD via a known-good probe module.
 * Supported by Chrome 91+/Firefox 89+/Safari 16.4+.
 * Probe = module with a function returning v128 via v128.const.
 */
export function detectWasmSimd(): boolean {
  try {
    // Canonical probe sequence (GoogleChromeLabs/wasm-feature-detect, MIT):
    //   (module (func (result v128)
    //     i32.const 0
    //     i8x16.splat          ;; 0xfd 0x0f
    //     i8x16.popcnt         ;; 0xfd 0x62
    //   ))
    // This exercises two SIMD ops so runtimes that advertise SIMD partially
    // (rare) still get caught.
    const bytes = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0,       // \0asm + v1
      1, 5, 1, 96, 0, 1, 123,             // type: () -> v128
      3, 2, 1, 0,                          // funcs: 1 of type 0
      10, 10, 1, 8, 0,                     // code section, body_size=8, locals=0
      0x41, 0,                             // i32.const 0
      0xfd, 0x0f,                          // i8x16.splat
      0xfd, 0x62,                          // i8x16.popcnt
      0x0b,                                // end
    ])
    return WebAssembly.validate(bytes)
  } catch {
    return false
  }
}

/**
 * Detect WASM bulk-memory (memory.copy / memory.fill / memory.init).
 * Chrome 75+/FF 79+/Safari 15+. Oxigraph 0.5.x requires this.
 *
 * Probe: module with a memory + function that uses memory.copy. The
 * memory.copy opcode (0xfc 0x0a 0 0) is part of the bulk-memory proposal
 * and is only decoded by runtimes that implement it.
 */
export function detectWasmBulkMemory(): boolean {
  try {
    // (module (memory 1) (func (memory.copy (i32.const 0) (i32.const 0) (i32.const 0))))
    const bytes = new Uint8Array([
      0, 97, 115, 109, 1, 0, 0, 0, // magic + version
      1, 4, 1, 96, 0, 0,            // type: () -> ()
      3, 2, 1, 0,                   // func 0 of type 0
      5, 3, 1, 0, 1,                // memory: min=1
      10, 14, 1, 12, 0,             // code hdr (body len 12)
      0x41, 0, 0x41, 0, 0x41, 0,    // i32.const 0 ×3
      0xfc, 0x0a, 0, 0,             // memory.copy (dst_mem=0, src_mem=0)
      0x0b,                         // end
    ])
    return WebAssembly.validate(bytes)
  } catch {
    return false
  }
}

export function detectCapabilities(): CoreCapabilities {
  const sab = detectSAB()
  const simd = detectWasmSimd()
  const bulk = detectWasmBulkMemory()

  let chosenPath: CoreCapabilities["chosenPath"] = "unknown"
  if (simd && bulk && sab.available) chosenPath = "fast"
  else if (simd && bulk) chosenPath = "standard"
  else if (bulk) chosenPath = "fallback"

  return {
    crossOriginIsolated: sab.crossOriginIsolated,
    sharedArrayBuffer: sab.available,
    wasmSimd: simd,
    wasmBulkMemory: bulk,
    chosenPath,
  }
}

// ---------------------------------------------------------------------------
// Shape SHA — simple content hash of all shapes+ontology TTL (for telemetry).
// ---------------------------------------------------------------------------

async function sha256Hex(input: string): Promise<string> {
  // Web Crypto in browser + Node 20 both provide subtle.digest.
  const enc = new TextEncoder().encode(input)
  const subtle = (globalThis.crypto && globalThis.crypto.subtle) as SubtleCrypto | undefined
  if (!subtle) {
    // Degraded fallback — not cryptographically strong; only used when
    // WebCrypto is missing (unlikely in browsers or Node 20+).
    let h = 0
    for (let i = 0; i < input.length; i++) {
      h = (Math.imul(31, h) + input.charCodeAt(i)) | 0
    }
    return `fnv32-${(h >>> 0).toString(16).padStart(8, "0")}`
  }
  const buf = await subtle.digest("SHA-256", enc)
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
}

// ---------------------------------------------------------------------------
// Module-scope state
// ---------------------------------------------------------------------------

let wasmInitialized = false
let wasmInitPromise: Promise<void> | null = null
let lastCapabilities: CoreCapabilities | null = null
let currentStore: InstanceType<typeof OxigraphStore> | null = null
let hydratedTripleCount = 0
let cachedShapesTtl: string | null = null

export function getStore(): InstanceType<typeof OxigraphStore> | null {
  return currentStore
}

export function isReady(): boolean {
  return wasmInitialized && currentStore !== null
}

export function getLastCapabilities(): CoreCapabilities | null {
  return lastCapabilities
}

export function getShapesTtl(): string | null {
  return cachedShapesTtl
}

/**
 * Reset module state. Test-only — do not call from production code.
 */
export function _resetForTest(): void {
  wasmInitialized = false
  wasmInitPromise = null
  currentStore = null
  hydratedTripleCount = 0
  cachedShapesTtl = null
  lastCapabilities = null
  setShapeSha("unhydrated")
}

// ---------------------------------------------------------------------------
// initKgCore — main entry
// ---------------------------------------------------------------------------

/**
 * Initialize the KG core: compile Oxigraph WASM, create the Store, hydrate
 * from provided TTL sources. Idempotent — calling twice returns without
 * re-init once the first call resolves.
 *
 * Failure modes (surfaced as thrown Error):
 *   - WASM SIMD not available (Safari < 16.4) → surfaced before WASM load
 *   - WASM bulk-memory not available → surfaced before WASM load
 *   - Oxigraph compile error (corrupt/wrong .wasm) → wrapped with diagnostic
 *   - TTL parse error during hydrate → surfaced with source name
 */
export async function initKgCore(opts: InitKgCoreOpts = {}): Promise<InitResult> {
  const t0 = nowMs()

  const caps = detectCapabilities()
  lastCapabilities = caps
  if (opts.verbose) {
    // Non-blocking observational log; production apps can strip console.debug.
    console.debug("[cogo-kg-browser] capabilities:", caps, {
      core: KG_BROWSER_CORE_VERSION,
      oxigraph: OXIGRAPH_VERSION,
    })
  }

  if (!caps.wasmBulkMemory) {
    throw new Error(
      "[cogo-kg-browser] WASM bulk-memory not supported by this runtime; Oxigraph requires it. Upgrade to a modern browser (Chrome 75+/Firefox 79+/Safari 15+).",
    )
  }
  if (!caps.wasmSimd) {
    throw new Error(
      "[cogo-kg-browser] WASM SIMD not supported by this runtime; Oxigraph 0.5.x uses SIMD. Upgrade to a modern browser (Chrome 91+/Firefox 89+/Safari 16.4+).",
    )
  }

  if (wasmInitialized && currentStore) {
    // Already initialized — return cached result shape (without re-measuring
    // wasm/hydrate times; caller can call `versionInfo()` / `getStore()` for
    // status-only checks). The numbers here reflect a no-op re-init cost.
    return {
      capabilities: caps,
      loadedTriples: hydratedTripleCount,
      shapeSha: (await getCurrentShapeShaOrUnhydrated()),
      initMs: 0,
      wasmMs: 0,
      hydrateMs: 0,
    }
  }

  // Compile WASM (async unless opts.wasmSource is a compiled Module).
  const wasmT0 = nowMs()
  if (!wasmInitPromise) {
    wasmInitPromise = loadOxigraphWasm(opts)
  }
  await wasmInitPromise
  wasmInitialized = true
  const wasmMs = nowMs() - wasmT0

  // Now that WASM is initialized, the module's class/function exports are
  // safe to bind. `loadOxigraphWasm` already executed `import("oxigraph/web.js")`
  // and populated `oxiWebMod`, so this is just a synchronous read.
  const oxi = getOxiWeb()

  // Create store + hydrate.
  const hydrateT0 = nowMs()
  currentStore = new oxi.Store()
  hydratedTripleCount = 0
  let hashInput = ""

  for (const src of opts.ttlSources ?? []) {
    hashInput += `# ${src.name}\n${src.ttl}\n`
    const quads = oxi.parse(src.ttl, { format: "text/turtle" })
    for (const q of quads) {
      currentStore.add(q)
      hydratedTripleCount++
    }
  }
  if (opts.shapesTtl) {
    cachedShapesTtl = opts.shapesTtl
    hashInput += `# shapes\n${opts.shapesTtl}\n`
  }
  const hydrateMs = nowMs() - hydrateT0

  const sha = hashInput ? await sha256Hex(hashInput) : "empty"
  setShapeSha(sha)

  // Phase 0431 D-C6: register /version polling timer for cache-invalidation
  // BroadcastChannel emission. First poll captures baseline silently;
  // subsequent polls broadcast on lastMigration change. Idempotent on
  // double-init. The timer is cleared on `beforeunload`. Deferred-import
  // here side-steps the index.ts <-> core-loader.ts circular dependency
  // (index.ts re-exports initKgCore from this module).
  try {
    const indexModule = await import("./index")
    indexModule.registerVersionPollingTimer({})
  } catch {
    /* Polling timer unavailable in this runtime — non-fatal */
  }

  return {
    capabilities: caps,
    loadedTriples: hydratedTripleCount,
    shapeSha: sha,
    initMs: nowMs() - t0,
    wasmMs,
    hydrateMs,
  }
}

async function loadOxigraphWasm(opts: InitKgCoreOpts): Promise<void> {
  // Lazy-load `oxigraph/web.js` here so a static import at the top of this
  // file does not exist for the loader (tsx) to choke on. Cached on the
  // first call; ESM module cache makes subsequent `import()` calls free.
  if (!oxiWebMod) {
    oxiWebMod = await import("oxigraph/web.js")
  }
  const initOxigraph = oxiWebMod.default
  const initSync = oxiWebMod.initSync
  if (opts.wasmSource !== undefined) {
    const src = opts.wasmSource
    if (src instanceof WebAssembly.Module) {
      // Synchronous init path — accepts a compiled Module directly.
      initSync({ module: src })
      return
    }
    // BufferSource: Oxigraph's async init accepts BufferSource.
    await initOxigraph({ module_or_path: src as any })
    return
  }
  if (opts.wasmUrl) {
    await initOxigraph({ module_or_path: opts.wasmUrl as any })
    return
  }
  // No explicit source/url — let Oxigraph's default init path try its
  // sibling `web_bg.wasm`. Works in dev/Vite/esbuild + Node unit tests
  // that have the file resolvable via import.meta.url.
  await initOxigraph()
}

async function getCurrentShapeShaOrUnhydrated(): Promise<string> {
  // `version.ts` stores current shape sha as module-private; we re-read via
  // versionInfo() to avoid exporting the setter outside this file's trust
  // boundary. Returns "unhydrated" if not yet set.
  const { versionInfo } = await import("./version")
  return versionInfo().shapeSha
}

function nowMs(): number {
  // performance.now is available in both browser and modern Node (via
  // globalThis.performance). Falls back to Date.now for ancient runtimes.
  const perf = (globalThis as any).performance
  if (perf && typeof perf.now === "function") return perf.now()
  return Date.now()
}
